import { Link } from "react-router-dom";
import { Star } from "lucide-react";
import { motion } from "framer-motion";
import { useCart, toLine } from "@/lib/cart";
import { money, discountPct, cleanTitle } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { Tilt } from "@/components/fx/Motion";
import { RatingSummary } from "./ProductReviews";
import { useXmasImage } from "@/lib/holiday";

export const Stars = ({ value = 5, size = 13 }) => (
  <div className="flex gap-0.5 text-gold">{[1, 2, 3, 4, 5].map((i) => <Star key={i} size={size} fill={i <= value ? "currentColor" : "none"} />)}</div>
);

export const Price = ({ price, compare, big }) => (
  <div className="flex items-baseline gap-2">
    <span className={`${big ? "text-2xl" : "text-base"} font-semibold text-white`}>{money(price)}</span>
    {compare > price && <span className={`${big ? "text-base" : "text-xs"} line-through text-white/40`}>{money(compare)}</span>}
  </div>
);

const CardImage = ({ product, pct }) => {
  const xmas = useXmasImage(product.handle);
  const first = xmas || product.images[0];
  const second = xmas ? product.images[0] : product.images[1];
  return (
  <Tilt className="aspect-[4/5] bg-white overflow-hidden">
    <Link to={`/products/${product.handle}`} className="absolute inset-0 block">
      <img data-testid={xmas ? `product-card-xmas-image-${product.handle}` : undefined} src={first} alt={product.title} loading="lazy" className={`absolute inset-0 h-full w-full ${xmas ? "object-cover" : "object-contain p-4"} transition-[transform,opacity] duration-[900ms] ease-out group-hover:scale-110 group-hover:opacity-0`} />
      {second && <img src={second} alt="" loading="lazy" className="absolute inset-0 h-full w-full object-contain p-4 opacity-0 scale-95 group-hover:opacity-100 group-hover:scale-100 transition-[transform,opacity] duration-[900ms] ease-out" />}
    </Link>
    {pct > 0 && <span data-testid={`product-card-badge-${product.handle}`} className="absolute top-3 left-3 bg-[#0b0a08] text-gold text-[10px] font-bold tracking-[0.15em] px-2.5 py-1.5">-{pct}%</span>}
    {!product.available && <span className="absolute top-3 right-3 bg-black text-white text-[10px] font-bold px-2 py-1">SOLD OUT</span>}
  </Tilt>
  );
};

export const ProductCard = ({ product, index = 0 }) => {
  const { add, checkout } = useCart();
  const { t } = useI18n();
  const v = product.variants[0];
  const single = product.variants.length === 1;

  return (
    <motion.div data-testid={`product-card-${product.handle}`} className="group flex flex-col"
      initial={{ opacity: 0, y: 60 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: (index % 4) * 0.09 }}>
      <CardImage product={product} pct={discountPct(product)} />
      <div className="pt-4 flex-1 flex flex-col">
        <p className="text-[10px] uppercase tracking-[0.15em] text-gold mb-1">{product.brand}</p>
        <Link to={`/products/${product.handle}`} className="text-sm lg:text-[15px] leading-snug hover:text-gold transition-colors line-clamp-2 min-h-[2.6em]">{cleanTitle(product.title)}</Link>
        <div className="mt-2 min-h-[16px]"><RatingSummary handle={product.handle} size={12} /></div>
        <div className="mt-2"><Price price={product.price} compare={product.compare_at_price} /></div>
        <div className="mt-4 grid grid-cols-2 gap-2">
          {single ? (
            <>
              <button data-testid={`product-card-add-${product.handle}`} disabled={!product.available} onClick={() => add(toLine(product, v))} className="btn-ghost h-11">{t("card_add")}</button>
              <button data-testid={`product-card-buy-now-${product.handle}`} disabled={!product.available} onClick={() => checkout([toLine(product, v)])} className="btn-gold btn-shine h-11">{t("buy_now")}</button>
            </>
          ) : (
            <Link data-testid={`product-card-choose-${product.handle}`} to={`/products/${product.handle}`} className="btn-gold h-11 col-span-2 flex items-center justify-center">{t("card_choose")}</Link>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export const ProductGrid = ({ products, loading, testid = "product-grid" }) => (
  <div data-testid={testid} className="grid grid-cols-2 lg:grid-cols-4 gap-x-4 gap-y-12 lg:gap-x-8">
    {loading
      ? Array.from({ length: 8 }).map((_, i) => <div key={i} className="aspect-[4/5] bg-white/5 animate-pulse" />)
      : products.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
  </div>
);
